import { RoleWatcher } from "PFApp/activities/types/watchers";
import {
  ActivityCustomField,
  ActivityPureCustomValue,
  ActivitySearchHighlights,
  Availability,
  AvailabilityRule,
  CustomValue,
  Engagement,
  Filters,
  Id,
  MatchedCustomValue,
  MatchScores,
  MatchScoresSnakeCase,
  PostUntilChangeReason,
  Profile,
  ShortlistMinimized,
  Snakify
} from "PFTypes";
import { SnakeCasedPropertiesDeep } from "type-fest";

import type { Role } from "./role";

export type ActivityProfile = {
  activity_id?: number;
  id: number;
  profile: Profile & { active?: boolean };
};

export enum ActivityProfileType {
  Owner = "owner",
  Reviewer = "reviewer",
  Assignee = "assignee"
}

export type Owner = ActivityProfile & { principal: boolean };
export type Reviewer = ActivityProfile & { unbiased: boolean };
export type Assignee = Pick<ActivityProfile, "id" | "profile">;

export type Metadata = Record<string, any> & {
  availability?: AvailabilityRule;
  filters?: Filters;
  interest_questions?: string[];
  interest_statement_enabled?: boolean;
  location?: string;
};

type ActivityStatistics = Record<
  "matches_count" | "interests_count" | "saves_count" | "shares_count" | "watchers_count",
  number
>;

export type ActivityState = "new" | "draft" | "complete";
export type ActivityWorkflowState =
  | "pending"
  | "shortlisting"
  | "auto_shortlisting"
  | "auto_filling"
  | "reviewing"
  | "inviting"
  | "partially_filled"
  | "filled"
  | "exception"
  | "partially_booked"
  | "booked"
  | "partially_confirmed"
  | "confirmed";
export type UIWorkflowState = ActivityWorkflowState | "not_filled";

export type ActivityReview = {
  deadline?: string;
  completed_at?: string;
  completed_by?: {
    id: number;
    first_name: string;
    last_name: string;
  };
  state: "complete" | "in_progress";
};

export type Audience = Record<string, Pick<CustomValue, "global_id" | "id" | "text" | "value">[]> & {
  unconfirmed?: boolean;
};

export type RollforwardProfile = { name: string; pills?: { custom_type_id: number; text: string }[] } & Pick<
  Profile,
  "id" | "avatar" | "suspended_at"
>;

export type RollforwardReference = {
  id: Id;
  name: string;
  booked_profiles?: RollforwardProfile[];
};

export type RollforwardInfo = {
  source?: RollforwardReference;
  targets?: RollforwardReference[];
};

// TODO: [SP-6843] Refactor Activity type to camelCase
export type Activity = {
  id: number;
  audiences: Audience[];
  availability?: Availability;
  booking_category_id?: Id | null;
  coowned: boolean;
  reviewed_by_requester?: boolean;
  custom_fields: ActivityCustomField[];
  developmental?: boolean;
  owners: Owner[];
  reviewers: Reviewer[];
  assignee: Assignee | null;
  template_id: number;
  name: string;
  description: string;
  subtemplate_key?: string;
  metadata: Metadata;
  statistics: ActivityStatistics;
  parent_activity_id: number | null;
  interest_id?: number;
  // participants are returned only from show activity endpoint,
  // they are missing on activities returned from index endpoint
  participants?: { profile: { id: number } }[];
  private: boolean;
  profile: Profile;
  review?: ActivityReview;
  shortlist_assignment?: ShortlistMinimized;
  state: ActivityState;
  skills_framework_ids?: Id[] | null;
  workflow_state: ActivityWorkflowState;
  children_workflow_states_tally?: Record<string, number>;
  source: "ui" | "jml";
  communication_type?: "group" | "direct";
  min_required_count: number;
  post_until: string;
  assigned: boolean;
  expires_soon: boolean;
  created_at: string;
  updated_at: string;
  post_until_change_reason?: PostUntilChangeReason | null;
  matched_at: string | null;
  reviewer?: boolean;
  scenarios_count: number;
  vacancies?: {
    total: number;
    closed: number;
  };
  shortlists?: { total: number; booked: number; confirmed: number };
  watchers?: RoleWatcher[];
  activity_tag: {
    id?: Id;
    value?: string;
    note?: string;
  };
  rollforward_info?: RollforwardInfo;
};

export type ActivityOption = {
  id: Id;
  name: string;
  description?: string;
  templateId: Id;
  parentActivityId?: Id;
  bookingCategoryId?: Id;
};

export type PostUntilChangeReasonData = Omit<PostUntilChangeReason, "text"> & { text?: string };

type ActivityCustomFieldData = {
  type: Partial<ActivityCustomField["type"]>;
  values: Pick<ActivityPureCustomValue, "id" | "text" | "value" | "importance" | "required_experience">[];
};

export type ActivityData = Omit<
  Partial<Activity>,
  "post_until_change_reason" | "metadata" | "custom_fields"
> & {
  custom_fields?: ActivityCustomFieldData[];
} & Partial<{
    post_until_change_reason: PostUntilChangeReasonData | null;
  }> &
  Partial<{
    metadata: Omit<Activity["metadata"], "availability"> &
      Partial<{ availability: AvailabilityRule | Record<string, never> }>;
  }>;

export type MarketplaceActivityCounter = "shares" | "saves" | "interests" | "matches";

export type MarketplaceActivityStatistics = Record<MarketplaceActivityCounter, number>;

type MarketplaceActivityAttributes = {
  matched_by: MatchPreference[];
  shared_id: number;
  saved_id: number;
  unread: boolean;
};

type MatchedCustomValues = {
  direct: CustomValue[];
  indirect: CustomValue[];
  // TODO: [SP-6843] Get rid of Snakify
  related: Snakify<Pick<MatchedCustomValue, "customValue" | "originalCustomValue">>[];
  parent: Snakify<Pick<MatchedCustomValue, "customValue" | "originalCustomValue">>[];
  child: Snakify<Pick<MatchedCustomValue, "customValue" | "originalCustomValue">>[];
};

export type MarketplaceActivity = {
  id: Id;
  activity: Pick<Activity, "id" | "name" | "interest_id" | "post_until" | "expires_soon"> &
    MarketplaceActivityAttributes;
  scores: MatchScoresSnakeCase | null;
  custom_values: MatchedCustomValues;
  optional_fields?: {
    highlights?: ActivitySearchHighlights;
  };
};

export type WorkflowActivity = {
  id: Id;
  activity: Activity | Role | Engagement;
  optional_fields?: {
    highlights?: ActivitySearchHighlights;
    term_score?: number;
  };
};

export enum MatchPreference {
  Duration = "duration",
  Skills = "skills",
  Type = "type"
}

type InterestDetailsData = {
  interest_details: {
    answer1: boolean | null;
    answer2: boolean | null;
    answer3: boolean | null;
    statement?: string;
  } | null;
};

export type MatchedActivity = {
  id: Id;
  activity: Omit<
    Activity,
    "audiences" | "source" | "assignee" | "owners" | "reviewers" | "shortlist_assignment"
  > &
    MarketplaceActivityAttributes &
    InterestDetailsData;
  scores: MatchScores | null;
  profile: {
    id: Id;
  };
  customValues: MatchedCustomValues;
  optionalFields?: {
    highlights?: ActivitySearchHighlights;
  };
};

export type MatchedActivitySnakeCase = SnakeCasedPropertiesDeep<MatchedActivity>;
